import { useState } from "react";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { RequestStep, StepOption, StepProgress } from "@/components/ui/request-step";
import { Clock } from "lucide-react";
import LocationPicker from "@/components/inquiry/LocationPicker";
import type { DachCity } from "@/components/inquiry/LocationPicker";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Header from "@/components/layout/Header";
import SEOHead from "@/components/seo/SEOHead";
import { useQuery } from "@tanstack/react-query";

type Step =
  | "trade"
  | "project_type"
  | "object_type"
  | "urgency"
  | "budget"
  | "description"
  | "location"
  | "contact"
  | "commit";

const stepOrder: Step[] = [
  "trade", "project_type", "object_type", "urgency", "budget",
  "description", "location", "contact", "commit",
];

const Inquiry = () => {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();

  const progressLabels = [t("inquiry.progressDetails"), t("inquiry.progressDescription"), t("inquiry.progressContact")];

  const projectTypeOptions = [
    { id: "repair", label: t("inquiry.repair"), description: t("inquiry.repairDesc") },
    { id: "new_build", label: t("inquiry.newBuild"), description: t("inquiry.newBuildDesc") },
    { id: "renovation", label: t("inquiry.renovation"), description: t("inquiry.renovationDesc") },
    { id: "installation", label: t("inquiry.installation"), description: t("inquiry.installationDesc") },
  ];

  const objectTypeOptions = [
    { id: "efh", label: t("inquiry.efh"), description: "" },
    { id: "mfh", label: t("inquiry.mfh"), description: "" },
    { id: "gewerbe", label: t("inquiry.commercial"), description: "" },
    { id: "industrie", label: t("inquiry.industrial"), description: "" },
    { id: "oeffentlich", label: t("inquiry.publicBuilding"), description: "" },
  ];

  const urgencyOptions = [
    { id: "immediate", label: t("inquiry.immediate"), description: t("inquiry.immediateDesc") },
    { id: "within_48h", label: t("inquiry.oneWeek"), description: t("inquiry.oneWeekDesc") },
    { id: "within_7_days", label: t("inquiry.oneMonth"), description: t("inquiry.oneMonthDesc") },
    { id: "planned", label: t("inquiry.flexible"), description: t("inquiry.flexibleDesc") },
  ];

  const budgetOptions = [
    { id: "under_2k", label: t("inquiry.under2k"), description: "" },
    { id: "2k_10k", label: t("inquiry.budget2k10k"), description: "" },
    { id: "10k_50k", label: t("inquiry.budget10k50k"), description: "" },
    { id: "over_50k", label: t("inquiry.over50k"), description: "" },
    { id: "unclear", label: t("inquiry.budgetUnclear"), description: "" },
  ];

  const { data: craftTrades = [] } = useQuery({
    queryKey: ["craft-trades"],
    queryFn: async () => {
      const { data } = await supabase
        .from("craft_trades")
        .select("id, key, label")
        .eq("is_active", true)
        .order("sort_order");
      return data || [];
    },
  });

  const [currentStep, setCurrentStep] = useState<Step>("trade");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [tradeCategory, setTradeCategory] = useState("");
  const [projectType, setProjectType] = useState("");
  const [objectType, setObjectType] = useState("");
  const [urgency, setUrgency] = useState("");
  const [budget, setBudget] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCity, setSelectedCity] = useState<DachCity | null>(null);
  const [desiredStartDate, setDesiredStartDate] = useState("");

  // Company info
  const [companyName, setCompanyName] = useState("");
  const [companyPostalCode, setCompanyPostalCode] = useState("");
  const [companyCity, setCompanyCity] = useState("");
  const [companyStreet, setCompanyStreet] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyNote, setCompanyNote] = useState("");

  // Primary contact
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactMobile, setContactMobile] = useState("");
  const [contactPosition, setContactPosition] = useState("");

  // Additional contact (optional)
  const [showAdditionalContact, setShowAdditionalContact] = useState(false);
  const [contact2Name, setContact2Name] = useState("");
  const [contact2Email, setContact2Email] = useState("");
  const [contact2Phone, setContact2Phone] = useState("");
  const [contact2Mobile, setContact2Mobile] = useState("");
  const [contact2Position, setContact2Position] = useState("");

  const currentIndex = stepOrder.indexOf(currentStep);
  const progressIndex = currentIndex <= 3 ? 0 : currentIndex <= 5 ? 1 : 2;

  const goNext = () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < stepOrder.length) {
      setCurrentStep(stepOrder[nextIndex]);
    }
  };

  const goBack = () => {
    const prevIndex = currentIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(stepOrder[prevIndex]);
    }
  };

  const handleCommit = async () => {
    setIsSubmitting(true);
    try {
      const contacts = [
        { name: contactName, email: contactEmail, phone: contactPhone, mobile: contactMobile, position: contactPosition },
      ];
      if (showAdditionalContact && contact2Name.trim()) {
        contacts.push({ name: contact2Name, email: contact2Email, phone: contact2Phone, mobile: contact2Mobile, position: contact2Position });
      }

      const contactData = {
        company: {
          name: companyName,
          postal_code: companyPostalCode,
          city: companyCity,
          street: companyStreet,
          phone: companyPhone,
          email: companyEmail,
          note: companyNote,
        },
        contacts,
      };

      const { error } = await supabase
        .from("requests")
        .insert({
          category: "project" as any,
          trade_category: tradeCategory || null,
          project_type: projectType || null,
          object_type: objectType || null,
          urgency: urgency as "immediate" | "within_48h" | "within_7_days" | "planned",
          budget_range: budget || null,
          description: description || null,
          context: description || null,
          decision_authority: "self" as any,
          location: selectedCity ? `${selectedCity.postalCode} ${selectedCity.name}` : null,
          location_text: selectedCity ? `${selectedCity.flag} ${selectedCity.name} (${selectedCity.postalCode})` : null,
          address_text: selectedCity ? `${selectedCity.postalCode} ${selectedCity.name}` : null,
          remote_allowed: false,
          onsite_required: true,
          requester_email: companyEmail,
          requester_company: companyName || null,
          contact_phone: companyPhone || null,
          desired_start_date: desiredStartDate || null,
          compliance_relevant: false,
          request_number: "",
          contact_data: contactData,
        } as any)
        .select()
        .single();

      if (error) throw error;

      toast.success(t("inquiry.submitSuccess"));
      navigate("/inquiry/danke");
    } catch (error) {
      console.error("Request error:", error);
      toast.error(t("inquiry.submitError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={t("inquiry.seoTitle")}
        description={t("inquiry.seoDesc")}
        canonical="/inquiry"
      />
      <Header />

      <div className="fixed top-16 md:top-24 left-0 right-0 section-padding z-40 bg-background/80 backdrop-blur-sm py-2">
        <div className="max-w-2xl mx-auto">
          <StepProgress steps={progressLabels} currentIndex={progressIndex} />
        </div>
      </div>

      <main id="main-content" className="min-h-[100svh] flex items-center justify-center section-padding pt-32 md:pt-36 pb-12" aria-live="polite">
        <div className="w-full max-w-xl">
          <div className="flex items-center justify-between mb-6">
            <p className="text-micro tracking-widest">
              {t("inquiry.questionOf", { current: currentIndex + 1, total: stepOrder.length })}
            </p>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {t("inquiryPsych.timePromise")}
            </p>
          </div>
          <p className="text-xs text-accent/80 text-center mb-4 italic">
            {t("inquiryPsych.qualityHint")}
          </p>

          {currentStep === "trade" && (
            <RequestStep key="trade" title={t("inquiry.tradeTitle")} subtitle={t("inquiry.tradeSubtitle")}>
              <div className="space-y-3">
                {craftTrades.map((trade) => {
                  const tradeKey = `inquiry.trade${trade.key.charAt(0).toUpperCase()}${trade.key.slice(1)}`;
                  const label = t(tradeKey, { defaultValue: trade.label });
                  return (
                  <StepOption
                    key={trade.key}
                    label={label}
                    isSelected={tradeCategory === trade.key}
                    onClick={() => {
                      setTradeCategory(trade.key);
                      setTimeout(goNext, 300);
                    }}
                  />
                  );
                })}
                {craftTrades.length === 0 && (
                  <p className="text-caption text-center py-4">{t("inquiry.tradesLoading")}</p>
                )}
              </div>
            </RequestStep>
          )}

          {currentStep === "project_type" && (
            <RequestStep key="project_type" title={t("inquiry.projectTypeTitle")} subtitle={t("inquiry.projectTypeSubtitle")}>
              <div className="space-y-4">
                {projectTypeOptions.map((option) => (
                  <StepOption
                    key={option.id}
                    label={option.label}
                    description={option.description}
                    isSelected={projectType === option.id}
                    onClick={() => {
                      setProjectType(option.id);
                      setTimeout(goNext, 300);
                    }}
                  />
                ))}
                <button onClick={goBack} className="w-full text-center py-3 text-caption link-subtle">{t("common.back")}</button>
              </div>
            </RequestStep>
          )}

          {currentStep === "object_type" && (
            <RequestStep key="object_type" title={t("inquiry.objectTypeTitle")} subtitle={t("inquiry.objectTypeSubtitle")}>
              <div className="space-y-4">
                {objectTypeOptions.map((option) => (
                  <StepOption
                    key={option.id}
                    label={option.label}
                    isSelected={objectType === option.id}
                    onClick={() => {
                      setObjectType(option.id);
                      setTimeout(goNext, 300);
                    }}
                  />
                ))}
                <button onClick={goBack} className="w-full text-center py-3 text-caption link-subtle">{t("common.back")}</button>
              </div>
            </RequestStep>
          )}

          {currentStep === "urgency" && (
            <RequestStep key="urgency" title={t("inquiry.urgencyTitle")} subtitle={t("inquiry.urgencySubtitle")}>
              <div className="space-y-4">
                {urgencyOptions.map((option) => (
                  <StepOption
                    key={option.id}
                    label={option.label}
                    description={option.description}
                    isSelected={urgency === option.id}
                    onClick={() => {
                      setUrgency(option.id);
                      setTimeout(goNext, 300);
                    }}
                  />
                ))}
                <button onClick={goBack} className="w-full text-center py-3 text-caption link-subtle">{t("common.back")}</button>
              </div>
            </RequestStep>
          )}

          {currentStep === "budget" && (
            <RequestStep key="budget" title={t("inquiry.budgetTitle")} subtitle={t("inquiry.budgetSubtitle")}>
              <div className="space-y-4">
                {budgetOptions.map((option) => (
                  <StepOption
                    key={option.id}
                    label={option.label}
                    isSelected={budget === option.id}
                    onClick={() => {
                      setBudget(option.id);
                      setTimeout(goNext, 300);
                    }}
                  />
                ))}
                <button onClick={goBack} className="w-full text-center py-3 text-caption link-subtle">{t("common.back")}</button>
                <button onClick={goNext} className="w-full text-center py-3 text-caption link-subtle">{t("common.skip")}</button>
              </div>
            </RequestStep>
          )}

          {currentStep === "description" && (
            <RequestStep key="description" title={t("inquiry.descriptionTitle")} subtitle={t("inquiry.descriptionSubtitle")}>
              <div className="space-y-4">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t("inquiry.descriptionPlaceholder")}
                  className="w-full min-h-[150px] p-4 border border-border bg-background text-body resize-none focus:border-primary transition-colors"
                  maxLength={5000}
                />
                <div className="flex items-center justify-between">
                  <span className="text-micro text-muted-foreground">{description.length}/5.000</span>
                  {description.length > 0 && description.length < 100 && (
                    <span className="text-xs text-destructive">{t("inquiryPsych.descShort")}</span>
                  )}
                  {description.length >= 100 && (
                    <span className="text-xs text-accent">{t("inquiryPsych.descGood")}</span>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-micro block">{t("inquiry.desiredStart")}</label>
                  <input
                    type="date"
                    value={desiredStartDate}
                    onChange={(e) => setDesiredStartDate(e.target.value)}
                    className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                  />
                </div>
                <div className="flex gap-4">
                  <Button variant="ghost" onClick={goBack}>{t("common.back")}</Button>
                  <Button variant="commit" className="flex-1" onClick={goNext}>{t("common.next")}</Button>
                </div>
              </div>
            </RequestStep>
          )}

          {currentStep === "location" && (
            <RequestStep key="location" title={t("inquiry.locationTitle")} subtitle={t("inquiry.locationSubtitle")}>
              <div className="space-y-6">
                <LocationPicker value={selectedCity} onChange={setSelectedCity} />
                <div className="flex gap-4">
                  <Button variant="ghost" onClick={goBack}>{t("common.back")}</Button>
                  <Button variant="commit" className="flex-1" onClick={goNext} disabled={!selectedCity}>{t("common.next")}</Button>
                </div>
              </div>
            </RequestStep>
          )}

          {currentStep === "contact" && (
            <RequestStep key="contact" title={t("inquiry.contactTitle")} subtitle={t("inquiry.contactSubtitle")}>
              <div className="space-y-8">
                {/* Company Information */}
                <div className="space-y-4">
                  <h3 className="text-micro uppercase tracking-wider">{t("inquiry.companyInfoTitle")}</h3>
                  <div className="space-y-2">
                    <label className="text-micro block">{t("inquiry.companyNameLabel")} *</label>
                    <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                      className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                      placeholder={t("inquiry.companyNamePlaceholder")} required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-micro block">{t("inquiry.companyPostalCode")}</label>
                      <input type="text" value={companyPostalCode} onChange={(e) => setCompanyPostalCode(e.target.value)}
                        className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                        placeholder={t("inquiry.postalCodePlaceholder", { defaultValue: "z.B. 42275" })} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-micro block">{t("inquiry.companyCity")}</label>
                      <input type="text" value={companyCity} onChange={(e) => setCompanyCity(e.target.value)}
                        className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                        placeholder={t("inquiry.companyCityPlaceholder")} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-micro block">{t("inquiry.companyStreet")}</label>
                    <input type="text" value={companyStreet} onChange={(e) => setCompanyStreet(e.target.value)}
                      className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                      placeholder={t("inquiry.companyStreetPlaceholder")} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2 min-w-0">
                      <label className="text-micro block">{t("inquiry.companyPhoneLabel")} *</label>
                      <input type="tel" value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)}
                        className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                        placeholder={t("inquiry.phonePlaceholder", { defaultValue: "+49 ..." })} required />
                    </div>
                    <div className="space-y-2 min-w-0">
                      <label className="text-micro block">{t("inquiry.companyEmailLabel")} *</label>
                      <input type="email" value={companyEmail} onChange={(e) => setCompanyEmail(e.target.value)}
                        className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                        placeholder={t("inquiry.emailPlaceholder", { defaultValue: "info@firma.de" })} required />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-micro block">{t("inquiry.companyNote")}</label>
                    <textarea value={companyNote} onChange={(e) => setCompanyNote(e.target.value)}
                      className="w-full min-h-[80px] p-4 border border-border bg-background text-body resize-none focus:border-primary transition-colors"
                      placeholder={t("inquiry.companyNotePlaceholder")} maxLength={1000} />
                  </div>
                </div>

                <div className="divider-line" />

                {/* Primary Contact */}
                <div className="space-y-4">
                  <h3 className="text-micro uppercase tracking-wider">{t("inquiry.contactPersonTitle")}</h3>
                  <div className="space-y-2">
                    <label className="text-micro block">{t("inquiry.contactNameLabel")} *</label>
                    <input type="text" value={contactName} onChange={(e) => setContactName(e.target.value)}
                      className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                      placeholder={t("inquiry.contactNamePlaceholder")} required />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2 min-w-0">
                      <label className="text-micro block">{t("inquiry.contactEmailLabel")} *</label>
                      <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                        className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                        placeholder={t("inquiry.contactEmailPlaceholder", { defaultValue: "name@firma.de" })} required />
                    </div>
                    <div className="space-y-2 min-w-0">
                      <label className="text-micro block">{t("inquiry.contactPhoneLabel")} *</label>
                      <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)}
                        className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                        placeholder={t("inquiry.contactPhonePlaceholder")} required />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2 min-w-0">
                      <label className="text-micro block">{t("inquiry.contactMobile")}</label>
                      <input type="tel" value={contactMobile} onChange={(e) => setContactMobile(e.target.value)}
                        className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                        placeholder={t("inquiry.mobilePlaceholder", { defaultValue: "+49 170 ..." })} />
                    </div>
                    <div className="space-y-2 min-w-0">
                      <label className="text-micro block">{t("inquiry.contactPositionLabel")}</label>
                      <input type="text" value={contactPosition} onChange={(e) => setContactPosition(e.target.value)}
                        className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                        placeholder={t("inquiry.contactPositionPlaceholder")} />
                    </div>
                  </div>
                </div>

                <div className="divider-line" />

                {/* Additional Contact (Optional) */}
                {!showAdditionalContact ? (
                  <button
                    onClick={() => setShowAdditionalContact(true)}
                    className="w-full py-3 border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
                  >
                    + {t("inquiry.addContact")}
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-micro uppercase tracking-wider">{t("inquiry.additionalContact")}</h3>
                      <button onClick={() => { setShowAdditionalContact(false); setContact2Name(""); setContact2Email(""); setContact2Phone(""); setContact2Mobile(""); setContact2Position(""); }}
                        className="text-xs text-muted-foreground hover:text-destructive transition-colors">
                        {t("common.cancel")}
                      </button>
                    </div>
                    <div className="space-y-2">
                      <label className="text-micro block">{t("inquiry.contactNameLabel")} *</label>
                      <input type="text" value={contact2Name} onChange={(e) => setContact2Name(e.target.value)}
                        className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                        placeholder={t("inquiry.contactNamePlaceholder")} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2 min-w-0">
                        <label className="text-micro block">{t("inquiry.contactEmailLabel")} *</label>
                        <input type="email" value={contact2Email} onChange={(e) => setContact2Email(e.target.value)}
                          className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                          placeholder={t("inquiry.contactEmailPlaceholder", { defaultValue: "name@firma.de" })} />
                      </div>
                      <div className="space-y-2 min-w-0">
                        <label className="text-micro block">{t("inquiry.contactPhoneLabel")} *</label>
                        <input type="tel" value={contact2Phone} onChange={(e) => setContact2Phone(e.target.value)}
                          className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                          placeholder={t("inquiry.contactPhonePlaceholder")} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2 min-w-0">
                        <label className="text-micro block">{t("inquiry.contactMobile")}</label>
                        <input type="tel" value={contact2Mobile} onChange={(e) => setContact2Mobile(e.target.value)}
                          className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                          placeholder={t("inquiry.mobilePlaceholder", { defaultValue: "+49 170 ..." })} />
                      </div>
                      <div className="space-y-2 min-w-0">
                        <label className="text-micro block">{t("inquiry.contactPositionLabel")}</label>
                        <input type="text" value={contact2Position} onChange={(e) => setContact2Position(e.target.value)}
                          className="w-full h-12 px-4 border border-border bg-background text-body focus:border-primary transition-colors"
                          placeholder={t("inquiry.contactPositionPlaceholder")} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-4">
                  <Button variant="ghost" onClick={goBack}>{t("common.back")}</Button>
                  <Button
                    variant="commit"
                    className="flex-1"
                    onClick={goNext}
                    disabled={
                      companyName.trim().length < 2 ||
                      companyPhone.trim().length < 6 ||
                      !companyEmail.includes("@") ||
                      contactName.trim().length < 2 ||
                      !contactEmail.includes("@") ||
                      contactPhone.trim().length < 6 ||
                      (showAdditionalContact && contact2Name.trim().length > 0 && (
                        !contact2Email.includes("@") || contact2Phone.trim().length < 6
                      ))
                    }
                  >
                    {t("inquiry.toSummary")}
                  </Button>
                </div>
              </div>
            </RequestStep>
          )}

          {currentStep === "commit" && (
            <RequestStep key="commit" title={t("inquiry.commitTitle")} subtitle="">
              <div className="space-y-8">
                <p className="text-body text-muted-foreground text-center">
                  {t("inquiry.commitDesc")}
                </p>

                <div className="p-6 border border-border bg-card">
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-caption">{t("inquiry.summaryTrade")}</span>
                      <span className="text-body">{t(`inquiry.trade${tradeCategory.charAt(0).toUpperCase()}${tradeCategory.slice(1)}`, { defaultValue: craftTrades.find(tr => tr.key === tradeCategory)?.label || tradeCategory })}</span>
                    </div>
                    <div className="divider-line" />
                    <div className="flex justify-between">
                      <span className="text-caption">{t("inquiry.summaryProjectType")}</span>
                      <span className="text-body">{projectTypeOptions.find(p => p.id === projectType)?.label}</span>
                    </div>
                    <div className="divider-line" />
                    <div className="flex justify-between">
                      <span className="text-caption">{t("inquiry.summaryUrgency")}</span>
                      <span className="text-body">{urgencyOptions.find(u => u.id === urgency)?.label}</span>
                    </div>
                    {selectedCity && (
                      <>
                        <div className="divider-line" />
                        <div className="flex justify-between">
                          <span className="text-caption">{t("inquiry.summaryLocation")}</span>
                          <span className="text-body">{selectedCity.flag} {selectedCity.name}</span>
                        </div>
                      </>
                    )}
                    <div className="divider-line" />
                    <div className="flex justify-between">
                      <span className="text-caption">{t("inquiry.companyInfoTitle")}</span>
                      <span className="text-body">{companyName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-caption">{t("inquiry.contactPersonTitle")}</span>
                      <span className="text-body">{contactName} ({contactEmail})</span>
                    </div>
                    {showAdditionalContact && contact2Name && (
                      <div className="flex justify-between">
                        <span className="text-caption">{t("inquiry.additionalContact")}</span>
                        <span className="text-body">{contact2Name} ({contact2Email})</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Trust signals before submit */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="flex items-center gap-2 p-3 bg-accent/5 border border-accent/10 rounded-sm">
                    <Clock className="w-4 h-4 text-accent shrink-0" />
                    <span className="text-xs text-muted-foreground">{t("inquiryPsych.trustFast", { defaultValue: "Antwort innerhalb 24h" })}</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-accent/5 border border-accent/10 rounded-sm">
                    <Clock className="w-4 h-4 text-accent shrink-0" />
                    <span className="text-xs text-muted-foreground">{t("inquiryPsych.trustFree", { defaultValue: "100% kostenlos & unverbindlich" })}</span>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-accent/5 border border-accent/10 rounded-sm">
                    <Clock className="w-4 h-4 text-accent shrink-0" />
                    <span className="text-xs text-muted-foreground">{t("inquiryPsych.trustVerified", { defaultValue: "Nur geprüfte Betriebe" })}</span>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button variant="ghost" onClick={goBack}>{t("common.back")}</Button>
                  <Button variant="commit" className="flex-1" onClick={handleCommit} disabled={isSubmitting}>
                    {isSubmitting ? t("inquiry.submitting") : t("inquiry.submitInquiry")}
                  </Button>
                </div>

                <p className="text-micro text-center text-muted-foreground">
                  {t("inquiryPsych.matchingHint")}
                </p>
              </div>
            </RequestStep>
          )}
        </div>
      </main>
    </div>
  );
};

export default Inquiry;
